import { AlertTriangle, CheckCircle2, Clock3, LifeBuoy, Loader2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { formatRail } from "@/shared/utils/railDisplay";

import { RETRY_ARM_DELAY_MS } from "../constants/payoutRescue.constants";
import { PaymentsPayoutService, PayoutStatus } from "../services/payoutRescue.service";
import type { RescuePayout, RetryOutcome } from "../types/payoutRescue.types";
import {
  destinationBeneficiary,
  destinationLabel,
  formatMoney,
  maskDestination,
  shortId,
} from "../utils/payoutRescueFormat";
import { RescueStatusPill } from "./RescueStatusPill";

export interface RetryPayoutModalProps {
  payout: RescuePayout;
  /** Resolved organization name (null when the catalog misses — the uuid tail is shown instead). */
  orgName: string | null;
  open: boolean;
  onClose: () => void;
  /** Fired once per attempt with the resolved outcome — the page reconciles lists/toasts from it. */
  onResolved: (outcome: RetryOutcome) => void;
}

type Stage = "form" | "sending" | "done";

function SummaryRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span
        className={
          mono
            ? "text-right font-mono text-sm text-foreground"
            : "text-right text-sm font-medium text-foreground"
        }
      >
        {value}
      </span>
    </div>
  );
}

/**
 * Trust-architecture retry confirmation (design brief §2.2): retrying is an irreversible money movement, so
 * the modal restates the full movement (beneficiary + masked destination when the snapshot is available,
 * corridor + org + amounts always), requires an explicit checkbox AND a reading delay before arming the
 * primary button, then reflects the REAL outcome from the dispatch response itself — `PROCESSING`, `PAID`
 * (synchronous settle), `FAILED` (terminal, funds auto-returned) or still-stuck on a pre-claim config error.
 * The backend claim is idempotent, so a concurrent double-click can never double-send; the button is still
 * disabled while in flight.
 */
export function RetryPayoutModal({ payout, orgName, open, onClose, onResolved }: RetryPayoutModalProps) {
  const { t, activeLanguage } = useTranslations("payoutRescue");

  const [stage, setStage] = useState<Stage>("form");
  const [checked, setChecked] = useState(false);
  const [armed, setArmed] = useState(false);
  const [outcome, setOutcome] = useState<RetryOutcome | null>(null);

  // Arm the confirm button only after the operator had time to read the summary (friction by design).
  // The page mounts this modal per attempt, so all stage state starts fresh on every open.
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => setArmed(true), RETRY_ARM_DELAY_MS);
    return () => clearTimeout(timer);
  }, [open, payout.id]);

  const money = (value: string) => `${formatMoney(value, activeLanguage)} ${payout.currency}`;
  const railLabel = formatRail(payout.rail);
  const corridor = `${payout.country} · ${railLabel} · ${payout.currency}`;
  const beneficiary = destinationBeneficiary(payout.destination);
  const destLabel = payout.destination ? destinationLabel(payout.destination) : null;
  const orgLabel = orgName ?? shortId(payout.organization_id);

  const confirm = async () => {
    if (!checked || !armed || stage !== "form") return;
    setStage("sending");
    try {
      const { data } = await PaymentsPayoutService.dispatchPayout(payout.id);
      const fresh = data as RescuePayout;
      const next: RetryOutcome =
        fresh.status === PayoutStatus.PAID
          ? { kind: "paid", payout: fresh }
          : fresh.status === PayoutStatus.PROCESSING
            ? { kind: "processing", payout: fresh }
            : fresh.status === PayoutStatus.FAILED
              ? { kind: "failed", payout: fresh }
              : // CANCELLED / REVERSED / anything else: someone resolved it first — a race, not an error.
                { kind: "race", payout: fresh };
      setOutcome(next);
      setStage("done");
    } catch (err: unknown) {
      // Dispatch throws PRE-claim only (provider not registered / corridor unsupported / bad destination
      // snapshot): the payout is untouched and still CREATED — retryable once platform fixes the config.
      setOutcome({
        kind: "stuck",
        message: HttpApiService.getErrorMessage(err) || t("retry_done_stuck_msg"),
      });
      setStage("done");
    }
  };

  const finish = () => {
    if (outcome) onResolved(outcome);
    onClose();
  };

  const doneMeta = (() => {
    switch (outcome?.kind) {
      case "paid":
        return {
          icon: <CheckCircle2 className="h-7 w-7" aria-hidden="true" />,
          tone: "text-success bg-success/10",
          title: t("retry_done_paid_title"),
          message: t("retry_done_paid_msg"),
          status: PayoutStatus.PAID as string,
        };
      case "processing":
        return {
          icon: <Clock3 className="h-7 w-7" aria-hidden="true" />,
          tone: "text-blue-600 bg-blue-500/10 dark:text-blue-300 dark:bg-blue-400/20",
          title: t("retry_done_processing_title"),
          message: t("retry_done_processing_msg"),
          status: PayoutStatus.PROCESSING as string,
        };
      case "failed":
        return {
          icon: <XCircle className="h-7 w-7" aria-hidden="true" />,
          tone: "text-destructive bg-destructive/10",
          title: t("retry_done_failed_title"),
          message: t("retry_done_failed_msg"),
          status: PayoutStatus.FAILED as string,
        };
      case "race":
        return {
          icon: <CheckCircle2 className="h-7 w-7" aria-hidden="true" />,
          tone: "text-muted-foreground bg-muted",
          title: t("retry_done_race_title"),
          message: t("retry_done_race_msg"),
          status: outcome.payout.status as string,
        };
      case "stuck":
        return {
          icon: <LifeBuoy className="h-7 w-7" aria-hidden="true" />,
          tone: "text-warning bg-warning/10",
          title: t("retry_done_stuck_title"),
          message: outcome.message,
          status: PayoutStatus.CREATED as string,
        };
      default:
        return null;
    }
  })();

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        // Never dismissible mid-flight: the operator must see the real outcome of a money movement.
        if (!next && stage !== "sending") {
          if (stage === "done") finish();
          else onClose();
        }
      }}
      title={t("retry_title")}
      size="default"
      showCloseButton={stage === "form"}
    >
      {stage === "form" && (
        <div className="space-y-4">
          <ColocaAlert variant="destructive" icon={AlertTriangle} text={t("retry_warning")} />

          <div className="rounded-xl border border-border bg-muted/40 px-4 py-3">
            <div className="divide-y divide-border/60">
              {beneficiary && <SummaryRow label={t("retry_summary_beneficiary")} value={beneficiary} />}
              {payout.destination && destLabel && (
                <SummaryRow
                  label={t(destLabel.key, destLabel.params)}
                  value={maskDestination(payout.destination)}
                  mono
                />
              )}
              <SummaryRow label={t("retry_summary_corridor")} value={corridor} />
              <SummaryRow label={t("retry_summary_org")} value={orgLabel} />
            </div>
            <div className="mt-3 border-t border-border pt-3 text-center">
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("retry_sends_label")}
              </p>
              <p className="mt-1 font-mono text-3xl font-bold tracking-tight text-foreground tabular-nums">
                {money(payout.net_amount)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t("retry_debits_caption", { gross: money(payout.gross_amount) })}
              </p>
            </div>
          </div>

          <label className="flex cursor-pointer items-start gap-2.5 select-none">
            <input
              type="checkbox"
              checked={checked}
              onChange={(event) => setChecked(event.target.checked)}
              className="mt-0.5 size-4 shrink-0 cursor-pointer accent-primary"
            />
            <span className="text-xs leading-relaxed text-muted-foreground">
              {payout.destination ? t("retry_confirm_check") : t("retry_confirm_check_no_destination")}
            </span>
          </label>

          <div className="flex justify-end gap-2 pt-1">
            <ColocaButton type="button" variant="ghost" onClick={onClose}>
              {t("retry_cancel")}
            </ColocaButton>
            <ColocaButton
              type="button"
              variant="primary"
              disabled={!checked || !armed}
              onClick={() => void confirm()}
            >
              {armed ? t("retry_confirm") : t("retry_arming")}
            </ColocaButton>
          </div>
        </div>
      )}

      {stage === "sending" && (
        <div className="flex flex-col items-center gap-4 py-8 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden="true" />
          <div>
            <p className="text-base font-semibold text-foreground">{t("retry_sending_title")}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("retry_sending_caption", { corridor: `${railLabel} · ${payout.currency}` })}
            </p>
          </div>
        </div>
      )}

      {stage === "done" && doneMeta && (
        <div className="flex flex-col items-center gap-4 py-4 text-center">
          <span className={`flex size-14 items-center justify-center rounded-full ${doneMeta.tone}`}>
            {doneMeta.icon}
          </span>
          <div>
            <p className="text-lg font-bold text-foreground">{doneMeta.title}</p>
            <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-muted-foreground">
              {doneMeta.message}
            </p>
          </div>
          <RescueStatusPill status={doneMeta.status} long />
          <ColocaButton type="button" variant="primary" className="w-full" onClick={finish}>
            {t("retry_done_button")}
          </ColocaButton>
        </div>
      )}
    </ColocaDialog>
  );
}
